import { useContext } from "react";

import type { ReasonableAdjustmentsFlagResponse } from "@thalamos/aspire-common";

import { apiHooks } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";

/**
 * Fetches NHS Reasonable Adjustments Flag (RAF) data for a patient.
 *
 * Returns `{ flags: null }` when the call is skipped because the patient has
 * no NHS number or the session organisation does not have `rafEnabled`.
 *
 * The underlying axios-hooks instance is configured with `cache: false`, so
 * each mount triggers a fresh fetch — RAF responses are never cached.
 */
export function useNhsFlags({
  patientId,
  nhsNumber,
}: {
  patientId: string;
  nhsNumber: string | null | undefined;
}): {
  flags: ReasonableAdjustmentsFlagResponse | null;
  loading: boolean;
  error: unknown;
} {
  const userContext = useContext(LoggedInUserContext);
  const rafEnabled =
    userContext?.user?.sessionOrganisationConfiguration?.rafEnabled ?? false;

  const skip = !rafEnabled || !nhsNumber;

  const [{ data, loading, error, response }] = apiHooks.patients.getNhsFlags(
    patientId,
    skip,
  );

  if (skip) {
    return { flags: null, loading: false, error: null };
  }

  // HARD BLOCK (frontend): the server returns 403 (with a `reason` body) for
  // patients flagged sensitive on Spine, and for any other access failure.
  // Never surface flag data unless we got an explicit 200 with a well-formed
  // body — anything else (error, 403, non-200) renders nothing.
  if (error || !response || response.status !== 200) {
    return { flags: null, loading, error };
  }

  const flags = isValidFlagResponse(data) ? data : null;

  return {
    flags,
    loading,
    error,
  };
}

/**
 * Only the two known-good response shapes are renderable. A restricted /
 * unknown / partial payload returns false so nothing is ever shown.
 */
function isValidFlagResponse(
  data: unknown,
): data is ReasonableAdjustmentsFlagResponse {
  if (!data || typeof data !== "object") {
    return false;
  }
  const role = (data as { role?: unknown }).role;
  return role === "clinical" || role === "non-clinical";
}
